import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, ArrowRight, Truck } from "lucide-react";
import AuthLayout from "../components/AuthLayout";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

export default function DriverLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    try {
      await login(email, password, "driver");
      toast({ title: "Welcome", description: "You're signed in to the driver portal." });
      navigate("/driver-dashboard");
    } catch (err) {
      toast({
        title: "Sign-in failed",
        description: err?.message?.replace("Firebase: ", "") || "Please check your credentials.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      variant="driver"
      title="Driver sign in"
      subtitle="Pick up where you left off — view today's routes and log your stops."
      footer={
        <>
          Need a driver login?{" "}
          <Link to="/driver-register" className="text-[#f59321] font-semibold hover:underline">
            Create driver account
          </Link>
          {" · "}
          <Link to="/driver-signup" className="text-[#f59321] font-semibold hover:underline">
            Apply to drive
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-5">
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#f59321]/10 border border-[#f59321]/30 text-sm text-[#0f2847]">
          <Truck className="w-4 h-4 text-[#f59321]" />
          You are signing in as a 518 driver.
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#0f2847] font-semibold">Driver email or ID</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="driver@518courier.com or DRV-001"
              className="h-12 pl-10 rounded-xl border-slate-200 focus:border-[#f59321] focus:ring-[#f59321]"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-[#0f2847] font-semibold">Password</Label>
            <Link to="/forgot-password" className="text-xs font-semibold text-[#f59321] hover:underline">
              Forgot?
            </Link>
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="h-12 pl-10 rounded-xl border-slate-200 focus:border-[#f59321] focus:ring-[#f59321]"
            />
          </div>
        </div>
        <Button
          type="submit"
          disabled={submitting}
          className="btn-lift w-full bg-[#f59321] hover:bg-[#e07f10] text-white font-semibold rounded-full h-12 shadow-md disabled:opacity-60"
        >
          {submitting ? "Signing in…" : "Sign in"}
          <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
        <div className="text-center text-xs text-slate-500">
          Secured by Firebase Authentication
        </div>
      </form>
    </AuthLayout>
  );
}
